import { resolveValueFormat } from '@highereducation/hen-ui';

import { hasTemplate } from '../../interpolation/hasTemplate';
import { interpolate } from '../../interpolation/interpolate';
import type { DataTableColumnDef } from '../types';
import { metricNumberFormat, type ValueFormat } from './valueFormat';

const EMPTY_CELL = '—';

export interface ResolvedTableColumn {
  readonly accessorKey: string;
  readonly header: string;
  readonly enableSorting: boolean;
  readonly enableColumnFilter: boolean;
  readonly filterFn: (
    row: { getValue: (columnId: string) => unknown },
    columnId: string,
    selectedValues: unknown,
  ) => boolean;
  readonly meta: {
    readonly align: 'left' | 'center' | 'right';
  };
  readonly cell: (context: {
    getValue: () => unknown;
    row: { original: Record<string, unknown> };
  }) => string;
}

/**
 * The format a cell uses: a dimension map wins when the row's dimension
 * value is present, otherwise the column format.
 */
export function resolveCellFormat(
  column: DataTableColumnDef,
  row: Record<string, unknown>,
): ValueFormat | undefined {
  const perDimension = column.formatByDimension;
  if (!perDimension?.map) return column.format;
  const raw = row[perDimension.dimensionKey];
  if (raw === null || raw === undefined) return column.format;
  return perDimension.map[String(raw)] ?? column.format;
}

/**
 * Turns declarative columns into the column definitions a table renders.
 *
 * Headers interpolate with a missing token becoming an empty string. Cell
 * text comes from {@link ValueFormat}; null and undefined render as an em dash.
 */
/** Interpolated headers, in column order. This is the label context a table memo has to watch. */
export function dataTableHeaderKey(
  columns: readonly DataTableColumnDef[],
  context: unknown,
): string {
  let key = '';
  for (let index = 0; index < columns.length; index += 1) {
    if (index > 0) key += '\0';
    key += interpolateHeader(columns[index].header, context);
  }
  return key;
}

export function buildDataTableColumns(
  columns: readonly DataTableColumnDef[],
  context: unknown,
): ResolvedTableColumn[] {
  return columns
    .filter((column) => isRenderable(column, context))
    .map((column) => ({
      accessorKey: column.accessorKey,
      header: interpolateHeader(column.header, context),
      enableSorting: column.sortable === true,
      enableColumnFilter: column.filterable === true,
      filterFn: (row, columnId, selectedValues) =>
        !Array.isArray(selectedValues) ||
        selectedValues.includes(row.getValue(columnId)),
      meta: {
        align: column.align ?? 'left',
      },
      cell: ({ getValue, row }) =>
        formatCell(getValue(), resolveCellFormat(column, row.original)),
    }));
}

function isRenderable(column: DataTableColumnDef, context: unknown): boolean {
  if (column.accessorKey.length > 0) return true;
  return interpolateHeader(column.header, context).length > 0;
}

function interpolateHeader(header: string, context: unknown): string {
  if (!hasTemplate(header)) return header;
  const resolved = interpolate(header, context, { onMissing: 'empty' });
  if (typeof resolved === 'string') return resolved;
  if (resolved === undefined || resolved === null) return '';
  return String(resolved);
}

function formatCell(value: unknown, format: ValueFormat | undefined): string {
  if (value === null || value === undefined) return EMPTY_CELL;
  if (format === undefined || format === 'raw') return String(value);

  const spec = metricNumberFormat(format);
  if (!spec) return String(value);
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return EMPTY_CELL;
  return resolveValueFormat(spec)?.(numeric) ?? String(value);
}
